import {useMemo, useState} from 'react'
import {Card} from 'react-bootstrap'
import Select from 'react-select'
import {useMutation, useQuery} from '@apollo/client'
import {defineMessages, useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {GET_ONBOARDING_UPLINE_CANDIDATES} from '../../../gql/queries/agentOnboardingQueries'
import {SET_AGENT_ONBOARDING_UPLINE} from '../../../gql/mutations/agentOnboardingMutations'
import {IUplineCandidate} from '../../../types/agentOnboarding'

const M = defineMessages({
  title: {id: 'onboarding.upline.title', defaultMessage: 'Upline'},
  subtitle: {
    id: 'onboarding.upline.subtitle',
    defaultMessage: 'Only agents with an NPN on file can be selected.',
  },
  current: {id: 'onboarding.upline.current', defaultMessage: 'Current upline'},
  none: {id: 'onboarding.upline.none', defaultMessage: 'No upline assigned'},
  change: {id: 'onboarding.upline.change', defaultMessage: 'Change upline'},
  search: {id: 'onboarding.upline.search', defaultMessage: 'Search by name, email or NPN…'},
  select: {id: 'onboarding.upline.select', defaultMessage: 'Select an agent'},
  save: {id: 'onboarding.upline.save', defaultMessage: 'Save upline'},
  clear: {id: 'onboarding.upline.clear', defaultMessage: 'Remove upline'},
  saved: {id: 'onboarding.upline.saved', defaultMessage: 'Upline updated'},
  failed: {id: 'onboarding.upline.failed', defaultMessage: 'Could not update the upline'},
  noMatches: {id: 'onboarding.upline.noMatches', defaultMessage: 'No agents match that search.'},
})

/** El correo es lo único que distingue a homónimos, y en este CRM hasta comparten NPN. */
const optionLabel = (c: IUplineCandidate) =>
  [c.name, c.email, c.npn ? `NPN ${c.npn}` : null, c.organizationName].filter(Boolean).join(' · ')

interface Props {
  applicationId: string
  currentUplineName: string | null
  canEdit: boolean
  onSaved: () => void
}

export function UplineSection({applicationId, currentUplineName, canEdit, onSaved}: Props) {
  const intl = useIntl()
  const {data} = useQuery<{onboardingUplineCandidates: IUplineCandidate[]}>(
    GET_ONBOARDING_UPLINE_CANDIDATES
  )
  const [setUpline, {loading: saving}] = useMutation(SET_AGENT_ONBOARDING_UPLINE)
  const [selectedId, setSelectedId] = useState('')

  const candidates = useMemo(() => data?.onboardingUplineCandidates ?? [], [data])

  const options = useMemo(
    () => candidates.map((c) => ({value: String(c.id), label: optionLabel(c)})),
    [candidates]
  )

  const persist = async (uplineId: string | null) => {
    try {
      const res = await setUpline({variables: {applicationId, uplineId}})
      const payload = res.data?.setAgentOnboardingUpline
      if (!payload?.success) {
        toast.error(payload?.errors?.[0]?.message ?? intl.formatMessage(M.failed))
        return
      }
      toast.success(intl.formatMessage(M.saved))
      setSelectedId('')
      onSaved()
    } catch {
      toast.error(intl.formatMessage(M.failed))
    }
  }

  return (
    <Card className='h-100'>
      <Card.Header className='border-0 pt-6'>
        <div>
          <h3 className='fw-bold text-gray-900 mb-1'>
            <i className='bi bi-diagram-3 me-2 text-primary' />
            {intl.formatMessage(M.title)}
          </h3>
          <span className='text-gray-600 fs-7'>{intl.formatMessage(M.subtitle)}</span>
        </div>
      </Card.Header>
      <Card.Body className='pt-2'>
        <div className='d-flex align-items-center gap-2 mb-4'>
          <span className='text-gray-500 fs-7'>{intl.formatMessage(M.current)}</span>
          {currentUplineName ? (
            <span className='badge badge-light-primary fw-bold fs-7'>{currentUplineName}</span>
          ) : (
            <span className='badge badge-light-warning fw-bold fs-7'>
              {intl.formatMessage(M.none)}
            </span>
          )}
        </div>

        {canEdit && (
          <>
            <div className='text-gray-600 fs-7 mb-2'>{intl.formatMessage(M.change)}</div>
            {/* react-select ya trae la búsqueda: el input propio y el <select size=6> sobraban. */}
            <Select
              className='mb-4'
              value={options.find((o) => o.value === selectedId) ?? null}
              options={options}
              placeholder={intl.formatMessage(M.search)}
              isClearable
              noOptionsMessage={() => intl.formatMessage(M.noMatches)}
              menuPortalTarget={document.body}
              styles={{menuPortal: (base) => ({...base, zIndex: 9999})}}
              onChange={(opt) => setSelectedId((opt as {value: string} | null)?.value ?? '')}
            />

            <div className='d-flex flex-wrap gap-3'>
              <button
                type='button'
                className='btn btn-primary'
                disabled={saving || !selectedId}
                onClick={() => persist(selectedId)}
              >
                <i className='bi bi-save me-1' />
                {intl.formatMessage(M.save)}
              </button>
              {currentUplineName && (
                <button
                  type='button'
                  className='btn btn-light-danger'
                  disabled={saving}
                  onClick={() => persist(null)}
                >
                  <i className='bi bi-x-lg me-1' />
                  {intl.formatMessage(M.clear)}
                </button>
              )}
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  )
}
